import { create } from 'zustand';
import { Currency } from '../types/api';
import { DEFAULT_BASE_CURRENCY, SUPPORTED_CURRENCIES, formatCurrency } from '../utils/currency';

const STORAGE_KEY = 'baseCurrency';

function readStored(): Currency {
  const raw = localStorage.getItem(STORAGE_KEY);
  return SUPPORTED_CURRENCIES.includes(raw as Currency)
    ? (raw as Currency)
    : DEFAULT_BASE_CURRENCY;
}

interface CurrencyStore {
  /**
   * The currency every cross-account TOTAL is reported in.
   *
   * Cached in localStorage so the first paint after a reload does not flash
   * CAD before the household loads -- a total that changes currency a beat
   * after appearing is worse than one that arrives late.
   */
  baseCurrency: Currency;
  /** Household this value came from, so a household switch re-fetches. */
  householdId: string | null;
  setBaseCurrency: (c: Currency, householdId?: string | null) => void;
}

export const useCurrencyStore = create<CurrencyStore>((set) => ({
  baseCurrency: readStored(),
  householdId: null,
  setBaseCurrency: (baseCurrency, householdId) => {
    if (!SUPPORTED_CURRENCIES.includes(baseCurrency)) return;
    localStorage.setItem(STORAGE_KEY, baseCurrency);
    set((s) => ({
      baseCurrency,
      householdId: householdId !== undefined ? householdId : s.householdId,
    }));
  },
}));

/**
 * The household base currency. Read this anywhere a TOTAL is displayed.
 *
 * Never use it to format a single account's balance -- that account has its
 * own `currency` field and converting it for display would misstate what the
 * bank actually holds.
 */
export function useBaseCurrency(): Currency {
  return useCurrencyStore((s) => s.baseCurrency);
}

/**
 * Format a base-currency total from a module-level helper, where a hook
 * cannot be called.
 *
 * Reads the store non-reactively, so a component using this MUST also call
 * useBaseCurrency() to subscribe -- otherwise changing the base currency
 * relabels nothing until the next remount. The subscription is what makes the
 * re-render happen; this call only supplies the value during it.
 */
export function baseMoney(
  amount: number | string | null | undefined,
  digits = 0
): string {
  const base = useCurrencyStore.getState().baseCurrency;
  return formatCurrency(amount, base, {
    base,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}
